import { cn } from '@/lib/classnames';
import type { PurchaseOrder } from '../../types';

interface ItemsTabProps {readonly po: PurchaseOrder;}

export function ItemsTab({ po }: ItemsTabProps) {
  const isDelivered = po.status === 'delivered';
  const colSpan = isDelivered ? 4 : 3;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Item
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                SKU
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                Qty
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                Unit Price
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                Total
              </th>
              {isDelivered && (
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                  Received
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {po.lines.map((line) => (
              <tr key={line.lineId} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{line.itemName}</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{line.sku}</td>
                <td className="px-4 py-3 text-right text-sm text-gray-900">
                  {line.quantity} {line.unit}
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-900">
                  ${line.unitPrice.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                  ${line.totalPrice.toFixed(2)}
                </td>
                {isDelivered && (
                  <td className="px-4 py-3 text-right text-sm">
                    <ReceivedQuantityCell line={line} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td
                colSpan={colSpan}
                className="px-4 py-3 text-right font-medium text-gray-900"
              >
                Subtotal
              </td>
              <td className="px-4 py-3 text-right font-medium text-gray-900">
                ${po.subtotal.toLocaleString()}
              </td>
              {isDelivered && <td />}
            </tr>
            <tr>
              <td
                colSpan={colSpan}
                className="px-4 py-3 text-right font-medium text-gray-900"
              >
                Tax
              </td>
              <td className="px-4 py-3 text-right font-medium text-gray-900">
                ${po.tax.toLocaleString()}
              </td>
              {isDelivered && <td />}
            </tr>
            <tr>
              <td
                colSpan={colSpan}
                className="px-4 py-3 text-right text-lg font-bold text-gray-900"
              >
                Total
              </td>
              <td className="px-4 py-3 text-right text-lg font-bold text-gray-900">
                ${po.total.toLocaleString()}
              </td>
              {isDelivered && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

interface POLine {
  readonly quantity: number;
  readonly receivedQuantity?: number;
  readonly unit: string;
}

function getColorClass(line: POLine): string {
  const isFullyReceived = line.receivedQuantity === line.quantity;
  const isPartiallyReceived =
    line.receivedQuantity !== undefined && line.receivedQuantity > 0;

  if (isFullyReceived) {
    return 'text-green-600';
  }
  if (isPartiallyReceived) {
    return 'text-amber-600';
  }
  return 'text-gray-400';
}

function ReceivedQuantityCell({ line }: { readonly line: POLine }) {
  const colorClass = getColorClass(line);

  return (
    <span className={cn('font-medium', colorClass)}>
      {line.receivedQuantity ?? '-'} {line.unit}
    </span>
  );
}
